import React, { useRef, useState } from "react";
import { FlaskConical, Upload, X, ChevronRight, AlertTriangle, FileCheck2 } from "lucide-react";

import { SCENARIOS, SCENARIO } from "../services/simulation";
import { SECTORS, ROUTES } from "../data/kumbhGeo";
import { ingestFile, PENDING_ADAPTERS } from "../services/ingest";

/* Scenario runner (§5) and data import (§10), in the left rail beneath the
 * layer controls.
 *
 * Simulation results are rendered in fuchsia throughout — the same hue the
 * SIMULATED trust badge uses — so a projection never looks like a reading,
 * even at a glance across a command room. */
export default function ScenarioPanel({
  lang = "en", onRun, onClear, result, pickMode, onPickModeChange, onImport,
}) {
  const [scenario, setScenario] = useState(SCENARIO.DENSITY_SURGE);
  const [sectorId, setSectorId] = useState(SECTORS[0]?.id);
  const [routeId, setRouteId] = useState(ROUTES[0]?.id);
  const [increase, setIncrease] = useState(25);
  const [radius, setRadius] = useState(3);
  const [importState, setImportState] = useState(null);
  const fileRef = useRef(null);

  const def = SCENARIOS.find((s) => s.key === scenario);
  const L = (o) => o?.[lang] ?? o?.en ?? "";

  const run = () => {
    if (def.needs === "sector") onRun(scenario, { sectorId, increasePct: increase });
    else if (def.needs === "route") onRun(scenario, { routeId });
    else onPickModeChange({ scenario, radiusKm: radius }); // waits for a map click
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportState({ busy: true });
    try {
      const res = await ingestFile(file);
      setImportState(res);
      if (res.ok) onImport?.(res);
    } catch (err) {
      setImportState({ ok: false, errors: [err.message], records: [] });
    } finally {
      e.target.value = ""; // allow re-importing the same file
    }
  };

  return (
    <div className="border-t border-slate-200">
      {/* ── Scenario runner ── */}
      <section className="px-3 py-3">
        <div className="flex items-center gap-1.5 mb-2">
          <FlaskConical size={12} className="text-fuchsia-600" />
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
            {lang === "mr" ? "डिजिटल ट्विन — काय-जर" : "Digital Twin — What If"}
          </span>
        </div>

        <select
          value={scenario}
          onChange={(e) => { setScenario(e.target.value); onPickModeChange(null); }}
          className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-[12px] font-semibold text-slate-700 bg-white"
        >
          {SCENARIOS.map((s) => (
            <option key={s.key} value={s.key}>{L(s.label)}</option>
          ))}
        </select>

        <p className="mt-1.5 text-[10px] leading-snug text-slate-500 italic">{L(def.question)}</p>

        {/* Scenario-specific inputs */}
        {def.needs === "sector" && (
          <div className="mt-2 space-y-2">
            <select
              value={sectorId}
              onChange={(e) => setSectorId(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-[12px] text-slate-700 bg-white"
            >
              {SECTORS.map((s) => (
                <option key={s.id} value={s.id}>{s.id} · {L(s.name)}</option>
              ))}
            </select>
            <label className="block">
              <span className="text-[10px] font-bold text-slate-500">
                {lang === "mr" ? "वाढ" : "Increase"}: +{increase}%
              </span>
              <input
                type="range" min={5} max={100} step={5}
                value={increase}
                onChange={(e) => setIncrease(Number(e.target.value))}
                className="w-full accent-fuchsia-600"
              />
            </label>
          </div>
        )}

        {def.needs === "route" && (
          <select
            value={routeId}
            onChange={(e) => setRouteId(e.target.value)}
            className="mt-2 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-[12px] text-slate-700 bg-white"
          >
            {ROUTES.map((r) => (
              <option key={r.id} value={r.id}>{r.id} · {L(r.name)}</option>
            ))}
          </select>
        )}

        {def.needs === "point" && (
          <>
            {scenario === SCENARIO.RESOURCES_NEAR && (
              <label className="block mt-2">
                <span className="text-[10px] font-bold text-slate-500">
                  {lang === "mr" ? "त्रिज्या" : "Radius"}: {radius} km
                </span>
                <input
                  type="range" min={1} max={10} step={0.5}
                  value={radius}
                  onChange={(e) => setRadius(Number(e.target.value))}
                  className="w-full accent-fuchsia-600"
                />
              </label>
            )}
            {pickMode && (
              <div className="mt-2 rounded-md border border-fuchsia-300 bg-fuchsia-50 px-2 py-1.5 text-[10px] font-bold text-fuchsia-800">
                {lang === "mr" ? "नकाशावर एक बिंदू निवडा…" : "Click a point on the map…"}
              </div>
            )}
          </>
        )}

        <div className="mt-2 flex gap-1.5">
          <button
            type="button"
            onClick={run}
            className="flex-1 rounded-lg bg-fuchsia-600 px-2 py-1.5 text-[11px] font-black text-white hover:bg-fuchsia-700 transition-colors"
          >
            {def.needs === "point"
              ? (lang === "mr" ? "बिंदू निवडा" : "Pick point")
              : (lang === "mr" ? "चालवा" : "Run")}
          </button>
          {result && (
            <button
              type="button"
              onClick={onClear}
              className="rounded-lg border border-slate-300 px-2 py-1.5 text-[11px] font-bold text-slate-600 hover:bg-slate-100"
            >
              <X size={12} />
            </button>
          )}
        </div>

        {result && <ScenarioResult lang={lang} result={result} L={L} />}
      </section>

      {/* ── Data import ── */}
      <section className="px-3 py-3 border-t border-slate-200">
        <div className="flex items-center gap-1.5 mb-2">
          <Upload size={12} className="text-slate-500" />
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
            {lang === "mr" ? "डेटा आयात" : "Import Data"}
          </span>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept=".geojson,.json,.csv,.tsv,.kml,.gpx"
          onChange={handleFile}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-full rounded-lg border border-dashed border-slate-300 px-2 py-2 text-[11px] font-bold text-slate-600 hover:border-slate-400 hover:bg-slate-50 transition-colors"
        >
          GeoJSON · CSV · KML · GPX
        </button>

        {importState?.busy && (
          <div className="mt-2 text-[10px] text-slate-500">Reading file…</div>
        )}

        {importState && !importState.busy && (
          <div className={`mt-2 rounded-md border px-2 py-1.5 ${
            importState.ok ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"
          }`}>
            <div className={`flex items-center gap-1 text-[10px] font-black ${
              importState.ok ? "text-emerald-800" : "text-red-800"
            }`}>
              {importState.ok ? <FileCheck2 size={11} /> : <AlertTriangle size={11} />}
              {importState.ok
                ? `${importState.records.length} record(s) imported as UNVERIFIED`
                : "Import failed"}
            </div>
            {importState.errors?.slice(0, 4).map((e) => (
              <div key={e} className="text-[9px] text-slate-600 mt-0.5 leading-snug">· {e}</div>
            ))}
            {importState.errors?.length > 4 && (
              <div className="text-[9px] text-slate-500 mt-0.5">
                …and {importState.errors.length - 4} more
              </div>
            )}
          </div>
        )}

        {/* Integration surface that needs a backend — listed so it is visible
            rather than silently missing. */}
        <details className="mt-2">
          <summary className="text-[10px] font-bold text-slate-400 cursor-pointer hover:text-slate-600">
            {lang === "mr" ? "बॅकएंड आवश्यक असलेले स्रोत" : "Sources needing a backend"}
          </summary>
          <ul className="mt-1 space-y-1">
            {PENDING_ADAPTERS.map((a) => (
              <li key={a.key} className="text-[9px] leading-snug text-slate-500">
                <span className="font-bold text-slate-600">{a.label}</span> — {a.needs}
              </li>
            ))}
          </ul>
        </details>
      </section>
    </div>
  );
}

/* Result rendering. Every number is prefixed as projected, and the
 * assumptions are shown rather than hidden behind a tooltip — they are the
 * part that decides whether the result means anything. */
function ScenarioResult({ lang, result, L }) {
  if (!result?.ok) {
    return (
      <div className="mt-2 rounded-md border border-red-300 bg-red-50 px-2 py-1.5 text-[10px] text-red-800">
        {result?.reason || "Scenario could not run"}
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg border-2 border-fuchsia-300 bg-fuchsia-50 p-2">
      <div className="flex items-center gap-1 mb-1.5">
        <span className="text-[8px] font-black px-1.5 py-0.5 rounded bg-fuchsia-200 text-fuchsia-900 border border-fuchsia-400">
          {lang === "mr" ? "प्रक्षेपित" : "PROJECTED"}
        </span>
        <span className="text-[9px] font-bold text-fuchsia-800 truncate">
          {L(result.subject?.name) || result.scenario}
        </span>
      </div>

      {result.scenario === SCENARIO.DENSITY_SURGE && (
        <>
          <div className="text-[10px] text-fuchsia-900 mb-1">
            +{result.increasePct}% → {result.newlyCritical.length}{" "}
            {lang === "mr" ? "झोन गंभीर होतील" : "zone(s) would turn critical"}
          </div>
          <div className="space-y-0.5">
            {result.rows.filter((r) => r.changed || r.id === result.subject.id).map((r) => (
              <div key={r.id} className="flex items-center justify-between text-[9px]">
                <span className="text-slate-700 truncate">{r.id}</span>
                <span className="font-bold tabular-nums">
                  {r.before.toLocaleString("en-IN")} → {r.after.toLocaleString("en-IN")}
                  <em className="ml-1 not-italic text-fuchsia-700">({r.ratioAfterPct}%)</em>
                </span>
              </div>
            ))}
          </div>
          {result.unabsorbed > 0 && (
            <div className="mt-1 text-[9px] font-bold text-red-700">
              {result.unabsorbed.toLocaleString("en-IN")}{" "}
              {lang === "mr" ? "लोक सामावले जाऊ शकत नाहीत" : "could not be absorbed anywhere"}
            </div>
          )}
        </>
      )}

      {result.scenario === SCENARIO.CLOSE_ROUTE && (
        <>
          <div className="text-[10px] text-fuchsia-900 mb-1">
            {result.affectedSectors.length}{" "}
            {lang === "mr" ? "सेक्टर प्रभावित" : "sector(s) affected"}
          </div>
          {result.alternatives.map((a) => (
            <div key={a.id} className="flex items-center justify-between text-[9px]">
              <span className="text-slate-700 truncate">{L(a.name)}</span>
              <span className="font-bold tabular-nums">{a.detourKm} km</span>
            </div>
          ))}
        </>
      )}

      {result.scenario === SCENARIO.EVACUATION && result.best && (
        <>
          <div className="text-[10px] font-bold text-fuchsia-900">{L(result.best.name)}</div>
          <div className="text-[9px] text-slate-700">
            {result.best.totalKm} km · ~{result.best.etaMinutes}{" "}
            {lang === "mr" ? "मिनिटे चालत" : "min on foot"}
          </div>
        </>
      )}

      {result.scenario === SCENARIO.RESOURCES_NEAR && (
        <>
          <div className="text-[10px] text-fuchsia-900 mb-1">
            {result.count} {lang === "mr" ? "संसाधने" : "resources"} ≤ {result.radiusKm} km
          </div>
          {Object.entries(result.byLayer).slice(0, 6).map(([layer, items]) => (
            <div key={layer} className="flex items-center justify-between text-[9px]">
              <span className="text-slate-700 truncate">{layer}</span>
              <span className="font-bold">{items.length}</span>
            </div>
          ))}
        </>
      )}

      <details className="mt-1.5">
        <summary className="text-[9px] font-bold text-fuchsia-700 cursor-pointer flex items-center gap-0.5">
          <ChevronRight size={8} />
          {lang === "mr" ? "गृहीतके" : "Assumptions"} ({result.assumptions.length})
        </summary>
        <ul className="mt-1 space-y-0.5">
          {result.assumptions.map((a) => (
            <li key={a} className="text-[9px] leading-snug text-slate-600">· {a}</li>
          ))}
        </ul>
      </details>
    </div>
  );
}
